import React, { Fragment, useMemo, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import {
  useCreateContingentMutation,
  useDeleteContingentMutation,
  useGetContingentStatusQuery,
  useListContingentsQuery,
  useUpdateContingentMutation,
} from '../../redux/api/contingentApi';
import { useListArticlesQuery } from '../../redux/api/articleApi';
import {
  useListBackstageLocationsQuery,
  useListCrewLocationsQuery,
} from '../../redux/api/locationApi';
import { useApiErrorToast } from '../../hooks/useApiError';
import { useHasGroup } from '../../hooks/useHasGroup';

const ContingentManagement = () => {
  const currentEvent = useSelector((s) => s.auth.currentEvent);
  const isStaff = useHasGroup('contingents');
  const toastError = useApiErrorToast();

  const [destinationType, setDestinationType] = useState('crew');
  const [articleFilter, setArticleFilter] = useState('');
  const [destinationFilter, setDestinationFilter] = useState('');

  const { data: contingents = [], isFetching } = useListContingentsQuery({
    eventId: currentEvent?.id,
    destinationType,
  });
  const { data: status = { results: [] } } = useGetContingentStatusQuery(
    { eventId: currentEvent?.id },
    { skip: !currentEvent?.id }
  );
  const { data: articles = [] } = useListArticlesQuery();
  const { data: crewLocations = [] } = useListCrewLocationsQuery(
    currentEvent?.id,
    { skip: !currentEvent?.id }
  );
  const { data: backstageLocations = [] } = useListBackstageLocationsQuery(
    currentEvent?.id,
    { skip: !currentEvent?.id }
  );

  const [createContingent] = useCreateContingentMutation();
  const [updateContingent] = useUpdateContingentMutation();
  const [deleteContingent] = useDeleteContingentMutation();

  // Key format `${destinationId}|${articleId}` must match the lookup below; both
  // sides are the backend's raw UUID strings (status row vs. contingent row).
  const consumedLookup = useMemo(() => {
    const map = {};
    (status.results || []).forEach((row) => {
      map[`${row.destination_id}|${row.article_id}`] = row;
    });
    return map;
  }, [status]);

  const locations =
    destinationType === 'crew' ? crewLocations : backstageLocations;
  const locationList = Array.isArray(locations) ? locations : locations?.results || [];
  const articleList = Array.isArray(articles) ? articles : articles?.results || [];

  const filteredRows = useMemo(() => {
    return (contingents.results || contingents || []).filter((c) => {
      if (articleFilter && !c.article_name?.toLowerCase().includes(articleFilter.toLowerCase())) {
        return false;
      }
      if (
        destinationFilter &&
        !c.destination?.name?.toLowerCase().includes(destinationFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [contingents, articleFilter, destinationFilter]);

  // New-row form state
  const [newDestId, setNewDestId] = useState('');
  const [newArticleId, setNewArticleId] = useState('');
  const [newQty, setNewQty] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newDestId || !newArticleId || newQty === '') return;
    try {
      await createContingent({
        destination_id: newDestId,
        article: newArticleId,
        quantity: parseInt(newQty, 10),
      }).unwrap();
      setNewQty('');
      // No manual refetch: the create mutation invalidates CONTINGENT_LIST.
    } catch (err) {
      toastError(err, 'Anlegen fehlgeschlagen.');
    }
  };

  const handleQuantityChange = async (row, value) => {
    const qty = parseInt(value, 10);
    if (Number.isNaN(qty) || qty < 0) return;
    try {
      await updateContingent({ id: row.id, quantity: qty }).unwrap();
    } catch (err) {
      toastError(err, 'Aktualisieren fehlgeschlagen.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Kontingent für ${row.article_name} löschen?`)) return;
    try {
      await deleteContingent(row.id).unwrap();
    } catch (err) {
      toastError(err, 'Löschen fehlgeschlagen.');
    }
  };

  return (
    <Fragment>
      <h4>Kontingente verwalten</h4>
      <p className='text-muted small'>
        Pro Lieferziel und Artikel können hier die geplanten Mengen hinterlegt werden.
        Kein Eintrag = unbegrenzt. Eintrag mit 0 = harter Stopp.
      </p>

      <div className='d-flex gap-3 mb-3 flex-wrap'>
        <Form.Select
          value={destinationType}
          onChange={(e) => setDestinationType(e.target.value)}
          style={{ maxWidth: 200 }}
        >
          <option value='crew'>Crew Locations</option>
          <option value='backstage'>Backstage Locations</option>
        </Form.Select>
        <Form.Control
          placeholder='Lieferziel filtern'
          value={destinationFilter}
          onChange={(e) => setDestinationFilter(e.target.value)}
          style={{ maxWidth: 240 }}
        />
        <Form.Control
          placeholder='Artikel filtern'
          value={articleFilter}
          onChange={(e) => setArticleFilter(e.target.value)}
          style={{ maxWidth: 240 }}
        />
      </div>

      <Table striped hover className='align-middle'>
        <thead>
          <tr>
            <th>Lieferziel</th>
            <th>Artikel</th>
            <th>Kontingent</th>
            <th>verbraucht</th>
            <th>noch übrig</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row) => {
            const consumed =
              consumedLookup[`${row.destination?.id}|${row.article}`]?.consumed ?? 0;
            const remaining = row.quantity - consumed;
            return (
              <tr key={row.id}>
                <td>{row.destination?.name}</td>
                <td>{row.article_name}</td>
                <td>
                  {isStaff ? (
                    <Form.Control
                      type='number'
                      min={0}
                      defaultValue={row.quantity}
                      onBlur={(e) => handleQuantityChange(row, e.target.value)}
                      style={{ maxWidth: 100 }}
                    />
                  ) : (
                    row.quantity
                  )}
                </td>
                <td>{consumed}</td>
                <td className={remaining < 0 ? 'text-danger fw-bold' : ''}>
                  {remaining}
                </td>
                <td>
                  {isStaff && (
                    <Button
                      variant='outline-danger'
                      size='sm'
                      onClick={() => handleDelete(row)}
                    >
                      Löschen
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
          {filteredRows.length === 0 && !isFetching && (
            <tr>
              <td colSpan={6} className='text-center text-muted'>
                Keine Kontingente konfiguriert.
              </td>
            </tr>
          )}
        </tbody>
        {isStaff && (
          <tfoot>
            <tr>
              <td>
                <Form.Select
                  value={newDestId}
                  onChange={(e) => setNewDestId(e.target.value)}
                >
                  <option value=''>Lieferziel wählen…</option>
                  {locationList.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Select
                  value={newArticleId}
                  onChange={(e) => setNewArticleId(e.target.value)}
                >
                  <option value=''>Artikel wählen…</option>
                  {articleList.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Control
                  type='number'
                  min={0}
                  value={newQty}
                  onChange={(e) => setNewQty(e.target.value)}
                  style={{ maxWidth: 100 }}
                />
              </td>
              <td colSpan={2} />
              <td>
                <Button onClick={handleCreate}>Anlegen</Button>
              </td>
            </tr>
          </tfoot>
        )}
      </Table>
    </Fragment>
  );
};

export default ContingentManagement;
